import { useEffect, useMemo, useRef, useState } from "react";
import { Alert, Button, DataTable, DetailList, Dialog, Field, StatusBadge, Tabs } from "../../../ui";
import type { DataColumn, FieldElement } from "../../../ui";
import type { Schemas } from "../../workspace/api.generated";
import { businessCodeLabel, date } from "../../workspace/model";
import { FilterBar, PageHeader, Panel, RowAction, RowLink } from "../../workspace/shared";
import { externalLogFilterSchema } from "../schema";
import type { ExternalLogCriteria, ExternalLogFilterValues, LogTab } from "../schema";

type QueryState = "idle" | "ready" | "loading" | "error";

function filterValues(criteria: ExternalLogCriteria): ExternalLogFilterValues {
  return { ...criteria, limit: String(criteria.limit) };
}

function auditResult(success: boolean) {
  return <StatusBadge variant={success ? "success" : "danger"}>{success ? "성공" : "실패"}</StatusBadge>;
}

export interface LogsViewProps {
  readonly tab: LogTab;
  readonly externalCriteria: ExternalLogCriteria;
  readonly audits?: readonly Schemas["AuditLogView"][];
  readonly auditState: QueryState;
  readonly externalLogs?: readonly Schemas["ExternalApiLogView"][];
  readonly externalState: QueryState;
  readonly selectedAudit?: Schemas["AuditLogView"];
  readonly selectedExternal?: Schemas["ExternalApiLogView"];
  readonly previewId?: number;
  readonly previewHref?: (id: number) => string;
  readonly onTabChange: (tab: LogTab) => void;
  readonly onExternalFiltersChange: (filters: ExternalLogFilterValues) => void;
  readonly onSelect: (id: number) => void;
  readonly onClosePreview: () => void;
  readonly onRetryAudit: () => void;
  readonly onRetryExternal: () => void;
}

export function LogsView(props: LogsViewProps) {
  const [draft, setDraft] = useState<ExternalLogFilterValues>(() => filterValues(props.externalCriteria));
  const [errors, setErrors] = useState<Record<string, string>>({});
  const refs = useRef<Record<string, FieldElement | null>>({});

  useEffect(() => {
    setDraft(filterValues(props.externalCriteria));
    setErrors({});
  }, [props.externalCriteria]);

  const updateDraft = (key: keyof ExternalLogFilterValues, value: string) => {
    setDraft((current) => ({ ...current, [key]: value }));
    setErrors((current) => ({ ...current, [key]: "" }));
  };
  const submitFilters = () => {
    const parsed = externalLogFilterSchema.safeParse(draft);
    if (!parsed.success) {
      const next: Record<string, string> = {};
      for (const issue of parsed.error.issues) next[String(issue.path[0])] ??= issue.message;
      setErrors(next);
      refs.current[String(parsed.error.issues[0]?.path[0])]?.focus();
      return;
    }
    props.onExternalFiltersChange(parsed.data);
  };

  const auditColumns = useMemo(() => [
    { key: "time", title: "발생일시", render: (row: Schemas["AuditLogView"]) => date(row.occurredAt) },
    { key: "actor", title: "실행자", render: (row: Schemas["AuditLogView"]) => `${businessCodeLabel(row.actorType)} ${row.actorType === "ADMIN" ? row.actorLoginId ?? "확인 불가" : row.actorId ?? "—"}` },
    { key: "action", title: "행동", render: (row: Schemas["AuditLogView"]) => businessCodeLabel(row.actionType) },
    { key: "target", title: "대상", render: (row: Schemas["AuditLogView"]) => `${businessCodeLabel(row.targetType)} ${row.targetId}` },
    { key: "result", title: "결과", render: (row: Schemas["AuditLogView"]) => auditResult(row.success) },
    { key: "select", title: "작업", render: (row: Schemas["AuditLogView"]) => props.previewHref ? <RowLink href={props.previewHref(row.id)} onNavigate={() => props.onSelect(row.id)} triggerKey={`audit-log:${row.id}:preview`}>감사 로그 {row.id} 선택</RowLink> : <RowAction onClick={() => props.onSelect(row.id)}>감사 로그 {row.id} 선택</RowAction> },
  ] satisfies readonly DataColumn<Schemas["AuditLogView"]>[], [props.onSelect, props.previewHref]);

  const externalColumns = useMemo(() => [
    { key: "time", title: "기록일시", render: (row: Schemas["ExternalApiLogView"]) => date(row.loggedAt) },
    { key: "request", title: "요청 ID", render: (row: Schemas["ExternalApiLogView"]) => <code>{row.requestId}</code> },
    { key: "provider", title: "Provider", render: (row: Schemas["ExternalApiLogView"]) => `${row.providerCode} · ${businessCodeLabel(row.environment)}` },
    { key: "api", title: "API", render: (row: Schemas["ExternalApiLogView"]) => `${row.httpMethod} ${row.endpointPath}` },
    { key: "ref", title: "업무 대상", render: (row: Schemas["ExternalApiLogView"]) => row.businessRefType ? `${businessCodeLabel(row.businessRefType)} ${row.businessRefId ?? "—"}` : "—" },
    { key: "status", title: "HTTP", numeric: true, render: (row: Schemas["ExternalApiLogView"]) => row.httpStatus ?? "—" },
    { key: "select", title: "작업", render: (row: Schemas["ExternalApiLogView"]) => props.previewHref ? <RowLink href={props.previewHref(row.id)} onNavigate={() => props.onSelect(row.id)} triggerKey={`external-log:${row.id}:preview`}>외부 로그 {row.id} 선택</RowLink> : <RowAction onClick={() => props.onSelect(row.id)}>외부 로그 {row.id} 선택</RowAction> },
  ] satisfies readonly DataColumn<Schemas["ExternalApiLogView"]>[], [props.onSelect, props.previewHref]);

  const count = props.tab === "audit" ? props.audits?.length : props.externalLogs?.length;
  const auditContent = <Panel title="관리자 감사로그" description="서버가 제공한 최신 최대 500건을 조회합니다. 기간·전체 건수 검색은 현재 API에서 지원하지 않습니다.">
    <DataTable caption="관리자 감사로그" columns={auditColumns} rows={props.audits ?? []} rowKey={(row) => row.id} state={props.auditState === "idle" ? "loading" : props.auditState} onRetry={props.onRetryAudit} />
  </Panel>;
  const externalContent = <>
    <Panel title="외부 API 로그 검색" description="Provider와 요청·업무 식별자를 서버 검색 조건으로 사용합니다.">
      <FilterBar onSubmit={submitFilters}>
        <Field compact label="Provider 코드" value={draft.providerCode} onChange={(event) => updateDraft("providerCode", event.currentTarget.value)} />
        <Field compact label="요청 ID" value={draft.requestId} onChange={(event) => updateDraft("requestId", event.currentTarget.value)} />
        <Field compact label="업무 유형" value={draft.businessRefType} onChange={(event) => updateDraft("businessRefType", event.currentTarget.value)} />
        <Field compact label="업무 대상 ID" value={draft.businessRefId} onChange={(event) => updateDraft("businessRefId", event.currentTarget.value)} />
        <Field ref={(element) => { refs.current.limit = element; }} compact inputMode="numeric" label="조회 건수" required value={draft.limit} error={errors.limit} onChange={(event) => updateDraft("limit", event.currentTarget.value)} />
      </FilterBar>
    </Panel>
    <Panel title="외부 API 호출이력" description="요청·응답 본문 없이 서버가 제공한 메타데이터만 표시합니다.">
      <DataTable caption="외부 API 호출이력" columns={externalColumns} rows={props.externalLogs ?? []} rowKey={(row) => row.id} state={props.externalState === "idle" ? "loading" : props.externalState} onRetry={props.onRetryExternal} />
    </Panel>
  </>;

  return <>
    <PageHeader id="ADM-S19" title="운영 로그" description="관리자 처리와 외부 API 호출 이력을 메타데이터로 추적합니다." meta={<p aria-live="polite">현재 조회 범위 {count ?? 0}건</p>} />
    <Tabs value={props.tab} onChange={(next) => props.onTabChange(next as LogTab)} items={[
      { id: "audit", label: "감사로그", content: auditContent },
      { id: "external", label: "외부 API 호출이력", content: externalContent },
    ]} />

    <Dialog open={props.previewId !== undefined} size="medium" title={props.selectedAudit ? `감사 로그 ${props.selectedAudit.id}` : props.selectedExternal ? `외부 API 로그 ${props.selectedExternal.id}` : `로그 ${props.previewId ?? ""}`} description="현재 조회 결과에서 선택한 로그의 메타데이터입니다." onClose={props.onClosePreview} primaryAction={<Button size="small" onClick={props.onClosePreview}>미리보기 닫기</Button>}>
    {props.selectedAudit ? <Panel title="감사 로그 상세 정보" description={date(props.selectedAudit.occurredAt)}>
      <DetailList items={[
        { label: "실행자", value: `${businessCodeLabel(props.selectedAudit.actorType)} ${props.selectedAudit.actorType === "ADMIN" ? props.selectedAudit.actorLoginId ?? "확인 불가" : props.selectedAudit.actorId ?? "—"}` },
        { label: "행동", value: businessCodeLabel(props.selectedAudit.actionType) },
        { label: "대상", value: `${businessCodeLabel(props.selectedAudit.targetType)} ${props.selectedAudit.targetId}` },
        { label: "처리 사유", value: props.selectedAudit.reason || "기록 없음" },
        { label: "결과", value: auditResult(props.selectedAudit.success) },
      ]} />
    </Panel> : null}

    {props.selectedExternal ? <Panel title="외부 API 로그 상세 정보" description={date(props.selectedExternal.loggedAt)}>
      <DetailList items={[
        { label: "요청 ID", value: <code>{props.selectedExternal.requestId}</code> },
        { label: "Provider", value: `${props.selectedExternal.providerCode} · ${businessCodeLabel(props.selectedExternal.environment)}` },
        { label: "API", value: `${props.selectedExternal.apiName} · ${props.selectedExternal.direction}/${props.selectedExternal.logType}` },
        { label: "Endpoint", value: `${props.selectedExternal.httpMethod} ${props.selectedExternal.endpointPath}` },
        { label: "업무 대상", value: props.selectedExternal.businessRefType ? `${businessCodeLabel(props.selectedExternal.businessRefType)} ${props.selectedExternal.businessRefId ?? "—"}` : "—" },
        { label: "회원 ID", value: props.selectedExternal.memberId ?? "—" },
        { label: "HTTP 상태", value: props.selectedExternal.httpStatus ?? "—" },
        { label: "Provider 결과 코드", value: props.selectedExternal.providerResultCode || "—" },
        { label: "처리시간", value: props.selectedExternal.processingTimeMs == null ? "—" : `${props.selectedExternal.processingTimeMs}ms` },
      ]} />
    </Panel> : null}
    {props.previewId !== undefined && !props.selectedAudit && !props.selectedExternal ? <Alert persistent variant="information" title="현재 조회 범위에 없는 로그입니다">조회 조건을 유지한 채 미리보기를 닫고 다른 항목을 선택해 주세요.</Alert> : null}
    </Dialog>

    <Alert persistent variant="information" title="공급자 원문은 표시하지 않습니다">요청·응답 payload와 Credential은 이 화면에서 조회하거나 복원할 수 없습니다.</Alert>
  </>;
}
